import React from 'react';
import { Col, Row, Table, Spin, Icon, Button, Alert } from 'antd';
import { Field } from 'redux-form';
import styled from 'styled-components';
import { NumberField } from 'redux-form-antd';
import { CheckboxField } from '../../../services/ant-redux';
import FormValueSelector from '../../../containers/FormValueSelector';

const antIcon = <Icon type="exclamation-circle-o" style={{ fontSize: 24 }} />;

const StyledTable = styled(Table)`
  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    word-break: inherit;
  }
`;

const StyledNumberField = styled(Field)`
  &:not(.ant-input-number-focused) {
    cursor: pointer !important;
    border: none !important;
    display: inline !important;

    input {
      cursor: pointer;
    }
  }
`;

const hideValues = (values, { id, title }, display) => {
  if (!title || !values || typeof title !== 'string') return null;
  const checkName = id ? `audience[${id}][included]` : `${title.toLowerCase()}_included`;

  return values(checkName) ? display : null;
};

const isNum = n =>
  !Number.isNaN(parseFloat(n)) && typeof parseFloat(n) === 'number' && parseFloat(n) >= 0;

const USD = n => `$${Number(n).toLocaleString()}`;

const ForecastResults = ({
  calculating,
  forecast,
  resultsData,
  forecastLength,
  calcCPM,
  calcTotalIE,
  calcDailyIE,
  calcTotalNetCost,
  CPEOrCPM,
}) => (
  <form name="ForecastResults">
    <Row type="flex">
      <Col xs={24} sm={24} md={24} lg={20} xl={18}>
        <Spin
          tip={
            <Row type="flex" justify="center" style={{ marginTop: 10 }}>
              <Col span={7}>
                <Alert
                  message="Revision pending"
                  description={
                    <div>
                      <p>Changes made to targeting will affect results.</p>
                      <Button
                        loading={calculating}
                        onClick={e => {
                          e.preventDefault();
                          window.document.getElementById('save-and-run').click();
                        }}
                      >
                        Save & Run Forecast
                      </Button>
                    </div>
                  }
                  type="info"
                />
              </Col>
            </Row>
          }
          indicator={antIcon}
          spinning={forecast.status === 0}
        >
          <FormValueSelector selector={values => values}>
            {values => (
              <StyledTable
                scroll={{ x: 800 }}
                pagination={false}
                size="small"
                dataSource={resultsData}
                rowKey={r => (typeof r.title === 'string' ? r.title : r.title.props.children)}
                columns={[
                  {
                    title: 'Included',
                    dataIndex: 'included',
                    render: (n, r) =>
                      n !== null ? (
                        <Field
                          name={
                            r.id
                              ? `audience[${r.id}][included]`
                              : `${r.title.toLowerCase()}_included`
                          }
                          format={a => !!a}
                          parse={a => (a ? 1 : 0)}
                          component={CheckboxField}
                        />
                      ) : null,
                  },
                  { title: 'Placement Name', dataIndex: 'title' },
                  {
                    title: 'Adjust Factor',
                    dataIndex: 'adjFactor',
                    render: (n, r) => {
                      if (r.title === 'inPath') {
                        return hideValues(
                          values,
                          r,
                          <span style={{ fontStyle: 'italic', padding: '0 11px' }}>{`${values(
                            'instore_adj_factor'
                          )}% inherited`}</span>
                        );
                      }

                      return typeof r.title !== 'string'
                        ? '-'
                        : hideValues(
                            values,
                            r,
                            <StyledNumberField
                              name={
                                r.id
                                  ? `audience[${r.id}][adjFactor]`
                                  : `${r.title.toLowerCase()}_adj_factor`
                              }
                              step={1}
                              max={100}
                              precision={0}
                              formatter={value => `${value}%`}
                              parse={value => `${value}`.replace('%', '')}
                              component={NumberField}
                            />
                          );
                    },
                  },
                  {
                    title: 'Daily Impressions / Engagements',
                    dataIndex: 'dailyIE',
                    render: (n, r) =>
                      typeof r.title !== 'string'
                        ? n.toLocaleString()
                        : hideValues(
                            values,
                            r,
                            isNum(calcDailyIE(r)) && calcDailyIE(r).toLocaleString()
                          ),
                  },
                  {
                    title: 'Total Impressions / Engagements',
                    dataIndex: 'totalIE',
                    render: (n, r) =>
                      typeof r.title !== 'string'
                        ? n.toLocaleString()
                        : hideValues(
                            values,
                            r,
                            isNum(calcTotalIE(r)) && calcTotalIE(r).toLocaleString()
                          ),
                  },
                  {
                    title: 'Cost',
                    dataIndex: 'cost',
                    render: (n, r) =>
                      typeof r.title !== 'string'
                        ? '-'
                        : hideValues(
                            values,
                            r,
                            <StyledNumberField
                              name={
                                r.id
                                  ? `audience[${r.id}][cost]`
                                  : `${r.title.toLowerCase()}_${CPEOrCPM(r).toLowerCase()}`
                              }
                              formatter={value =>
                                `$${calcCPM(r, value)} ${CPEOrCPM(r)}`.replace(
                                  /\B(?=(\d{3})+(?!\d))/g,
                                  ','
                                )
                              }
                              parse={value =>
                                `${calcCPM(r, value)}`
                                  .replace(/\$\s?|(,*)/g, '')
                                  .replace('CPE', '')
                                  .replace('CPM', '')
                              }
                              component={NumberField}
                            />
                          ),
                  },
                  {
                    title: 'Total Net Cost',
                    dataIndex: 'totalNet',
                    render: (n, r) =>
                      typeof r.title !== 'string'
                        ? USD(n)
                        : hideValues(
                            values,
                            r,
                            isNum(calcTotalNetCost(r)) && USD(calcTotalNetCost(r))
                          ),
                  },
                ]}
              />
            )}
          </FormValueSelector>
        </Spin>
      </Col>
    </Row>
  </form>
);

export default ForecastResults;
